import { useQuery } from '@tanstack/react-query';
import { SUPPORTBFF_URL } from '@zapier/product-get-help/src/constants';
import { KindType } from '@zapier/product-get-help/src/index';
import { useMemo } from 'react';
import { QUERY_KEY, SUPPORT_BFF_ENDPOINT } from '../constants';

type zapsNodes = {
  id: string;
  selectedApi: string | null;
  appImage64: string | null;
};

type Zaps = {
  // previously ZapData
  id: string;
  title: string | null;
  folderIds: string[];
  nodes: zapsNodes[];
  kind: string | null;
};

type FolderData = {
  id: string;
  isShared: boolean;
  title: string;
};

type ZapBlockAccount = {
  id: string;
  folders: FolderData[];
};

export type ZapBlock = {
  account: ZapBlockAccount;
  zaps: Zaps[];
};

type Input = {
  accountId: string | null;
  search: string;
  kind: KindType[];
};

export type ZapType = {
  id: string | null;
  title: string | null;
  nodes: {
    id: string;
    selectedApi: string | null;
    appImage64: string | null;
  }[];
};

export type DataType = {
  title: string;
  folders: {
    id: string;
    title: string;
    zaps: Zaps[];
  }[];
}[];

const HOME_FOLDER: FolderData = {
  id: '0',
  isShared: false,
  title: 'Home',
};

const DEFAULT_FOLDER_IDS = [HOME_FOLDER.id];

const FOLDER_GROUPS = [
  { title: 'Private Folders', isShared: false },
  {
    title: 'Shared Folders',
    isShared: true,
  },
];

const zapCompare = (first: Zaps, second: Zaps) =>
  (first.title || '').localeCompare(second.title || '');

const filterZaps = (zaps: Zaps[], search: string) => {
  if (!search) {
    return zaps;
  }

  const searchLowerCase = search.toLowerCase();

  return zaps.filter(
    ({ title }) => title && title.toLowerCase().includes(searchLowerCase)
  );
};

const buildZapsByFolder = (
  data: ZapBlock,
  search: string
): Map<string, Zaps[]> => {
  // Null values from supportbff are allowed.
  // And for some reason `?.` breaks flow type generation
  const zaps = ((data && data.zaps && data.zaps.filter(zap => !!zap)) ||
    []) as Zaps[];

  const sortedZaps = zaps.sort(zapCompare);
  const filteredZaps = filterZaps(sortedZaps, search);
  const zapsByFolder = filteredZaps.reduce((map, zap) => {
    const folderIds =
      zap.folderIds.length === 0 ? DEFAULT_FOLDER_IDS : zap.folderIds;
    folderIds.forEach(folderId => {
      const folderZaps = map.get(folderId);
      if (folderZaps) {
        folderZaps.push(zap);
      } else {
        map.set(folderId, [zap]);
      }
    });
    return map;
  }, new Map<string, Zaps[]>());
  return zapsByFolder;
};

const folderCompare = (first: FolderData, second: FolderData) => {
  // Home comes first
  if (first.id === '0') {
    return -1;
  } else if (second.id === '0') {
    return 1;
  }
  // Sort by title
  return first.title.localeCompare(second.title);
};

const buildFolders = (data: ZapBlock) => {
  // For some reason `?.` breaks flow type generation
  const folders = (data && data.account && data.account.folders) || [];
  const foldersWithHome = [HOME_FOLDER, ...folders];
  return foldersWithHome.sort(folderCompare);
};

const buildFoldersWithZaps = (
  folders: FolderData[],
  zapsByFolder: Map<string, Zaps[]>,
  shouldBeShared: boolean,
  kindsToReturn: string[]
) =>
  folders.reduce<DataType[0]['folders']>((result, { id, isShared, title }) => {
    if (shouldBeShared !== isShared) {
      return result;
    }

    const zaps = zapsByFolder.get(id) || [];

    const zapsFilteredByKind = zaps.filter(
      zap => zap.kind !== null && kindsToReturn.includes(zap.kind)
    );

    if (zapsFilteredByKind.length === 0) {
      return result;
    }

    result.push({
      id,
      title,
      zaps: zapsFilteredByKind,
    });

    return result;
  }, []);

export const buildData = (data: ZapBlock, search: string, kind: KindType[]) => {
  const zapsByFolder = buildZapsByFolder(data, search);
  const sortedFolders = buildFolders(data);

  return FOLDER_GROUPS.reduce<DataType>((folderGroups, { title, isShared }) => {
    const foldersWithZaps = buildFoldersWithZaps(
      sortedFolders,
      zapsByFolder,
      isShared,
      kind
    );

    if (foldersWithZaps.length > 0) {
      folderGroups.push({
        title,
        folders: foldersWithZaps,
      });
    }

    return folderGroups;
  }, []);
};

const fetchZaps = async (
  accountId: string | null
): Promise<{
  zapData: ZapBlock | null;
  error: string | null;
}> => {
  try {
    const response = await fetch(
      `${SUPPORTBFF_URL}${SUPPORT_BFF_ENDPOINT}/?accountId=${accountId}`,
      {
        credentials: 'include',
      }
    );
    if (!response.ok) {
      throw new Error('Failed to fetch Zaps from supportbff.');
    }

    return { zapData: await response.json(), error: null };
  } catch (error) {
    let message = '';
    if (error instanceof Error) {
      message = error.message;
    }
    return { zapData: null, error: message };
  }
};

const useData = ({ accountId, search, kind }: Input) => {
  const { error, data, isLoading } = useQuery({
    queryKey: [QUERY_KEY, accountId],
    queryFn: () => fetchZaps(accountId),
    enabled: !!accountId,
    staleTime: 60000, // 60 seconds
  });

  const processedData = useMemo(() => {
    if (error || isLoading || !data || !data.zapData) {
      return [];
    }

    return buildData(data.zapData, search, kind);
  }, [error, isLoading, data, search, kind]);

  return {
    error,
    isLoading,
    data: processedData,
  };
};

export default useData;
